import React, { forwardRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { useUscSelector } from "../../../../redux/store";
import { useUscLabel } from "../../../../utils/labels";
import { UscDisclaimerType } from "../../../../../shared-logic/types/UscCommonTypes";
import { formatPriceIntl } from "../../../../../../common-deprecated/priceUtils";
import DisclaimerIconWrapper from "../../common/DisclaimerIconWrapper";
import { enableUscMonthlyRate } from "../../../../utils/settings";
import type { UscPackType } from "../../../../utils/accessoryUtils";
import { getAccessoryPackPriceSelector } from "../../../../utils/accessoryUtils";
import Card from "../../styles/molecules/Card";
import Text from "../../styles/atoms/Text";
import * as Styles from "./Packitem.styles";
import * as CardStyles from "../../styles/molecules/Card.styles";
import { trackPackShowFeaturesClick } from "../../../../utils/tracking";
import Collapse from "../../styles/molecules/Collapse";
import Button from "../../styles/atoms/Button";
// TODO: uncomment when implementing accessories in the extra step
// import Checkbox from "../../styles/atoms/Checkbox";
// import Icon from "../../styles/Icon";

const PackItem = forwardRef<HTMLDivElement, { pack: UscPackType; index: number }>(({ pack, index }, ref) => {
    const [showFeatures, setShowFeatures] = useState<boolean>(false);
    const packPrice = useUscSelector(getAccessoryPackPriceSelector(pack), shallowEqual);
    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const financeEnabled = useUscSelector(({ commonSettings: financeCommonSettings, uscSettings, vehicle }) =>
        enableUscMonthlyRate(financeCommonSettings, uscSettings, vehicle.result, vehicle.type),
    );
    const [loadingLabel, buttonShowFeaturesLabel, buttonHideFeaturesLabel] = useUscLabel([
        "uscLoading",
        "uscPdpPacksShowFeatures",
        "uscPdpPacksHideFeatures",
    ]);

    return (
        <Card variant="pack" cardRef={ref} data-index-number={index}>
            <CardStyles.Body>
                <Text tag="h4" variant="h6">
                    {pack.name}
                </Text>
                {!!pack.items?.length && (
                    <Collapse variant="default">
                        <Button
                            variant="link"
                            size="small"
                            aria-controls="collapse-packs"
                            aria-expanded={showFeatures}
                            onClick={() => {
                                if (!showFeatures) trackPackShowFeaturesClick(vehicleType, pack.name);
                                setShowFeatures(!showFeatures);
                            }}
                        >
                            {showFeatures ? buttonHideFeaturesLabel : buttonShowFeaturesLabel}
                        </Button>
                        <Styles.AccessoriesGrid id="collapse-packs" hidden={!showFeatures} aria-hidden={!showFeatures}>
                            {pack.description && (
                                <Styles.AccessoriesDescription>{pack.description}</Styles.AccessoriesDescription>
                            )}
                            {pack.items?.map((application) => (
                                <Card variant="accessories-in-pack">
                                    {application.imageUrl && <img src={application.imageUrl} alt="" />}
                                    {application.name && (
                                        <CardStyles.Body>
                                            <Text tag="h5" variant="h6">
                                                {application.name}
                                            </Text>
                                        </CardStyles.Body>
                                    )}
                                </Card>
                            ))}
                        </Styles.AccessoriesGrid>
                    </Collapse>
                )}
            </CardStyles.Body>
            {packPrice && (
                <CardStyles.Footer>
                    <div>
                        <Text tag="div" variant="paragraph-body-regular">
                            {packPrice.cashLoading
                                ? loadingLabel
                                : formatPriceIntl(commonSettings, packPrice.cashPrice.cashPriceInclVat).primaryPrice}
                            <DisclaimerIconWrapper type={UscDisclaimerType.Accessory} />
                        </Text>
                        {financeEnabled && !packPrice.monthlyLoading && !!packPrice.monthlyPrice && (
                            <Text tag="div" variant="paragraph-body-book" color="midnight">
                                <span dangerouslySetInnerHTML={{ __html: packPrice.monthlyPriceFormatted }} />
                            </Text>
                        )}
                    </div>
                    {/* TODO: uncomment when implementing accessories in the extra step */}
                    {/* <Checkbox variant="pack" id={`select-${pack.name}`} title={pack.name} checked={false} disabled={false}>
                    <Icon variant="plus" />
                    </Checkbox> */}
                </CardStyles.Footer>
            )}
        </Card>
    );
});

export default PackItem;
